import { useState } from "react";
import { Play, Youtube } from "lucide-react";
import { useLang } from "@/lib/i18n";
import Lightbox from "./Lightbox";

const VIDEO_ID = "GHvQv_uAsRg";
const VIDEO_URL = `https://www.youtube.com/watch?v=${VIDEO_ID}`;
const src = (p) => `/story/${p}`;

const PHOTOS = [
  ["IMG_9144", 1170, 1611, "Two performers on stage under spotlights, raising joined hands; one holds a microphone and the other an award.", "Deux artistes sur scène sous les projecteurs, mains jointes levées ; l’un tient un micro, l’autre un trophée."],
  ["IMG_9143", 1170, 1294, "A performer in a grey hat and shirt singing intensely into a microphone on stage.", "Un artiste en chapeau et chemise gris chante avec intensité dans un micro sur scène."],
  ["IMG_9146", 1170, 753, "Two judges seated at a table with a microphone and water bottles, watching a performance.", "Deux juges assis à une table avec un micro et des bouteilles d’eau, en train d’observer une performance."],
  ["IMG_9145", 1170, 1676, "A participant holding a “Thank you for being part of SHOW YOUR SKILLZ 2010” plaque in front of the event backdrop.", "Un participant tient une plaque « Thank you for being part of SHOW YOUR SKILLZ 2010 » devant la toile de fond de l’événement."],
].map(([id, w, h, alt_en, alt_fr]) => ({ id, w, h, alt_en, alt_fr, storage_path: `${id}.webp`, thumb_path: `${id}_thumb.webp` }));

const COPY = {
  en: { eyebrow: "Photo gallery", title: "SHOW YOUR SKILLZ — Where It All Began", intro: "A look back at the performances, people, and moments that brought Montréal’s creative community together.",
    vTitle: "Relive Our Last Edition", vDesc: "Watch the full show from our final SHOW YOUR SKILLZ edition.", play: "Play video", yt: "Watch on YouTube" },
  fr: { eyebrow: "Galerie photo", title: "SHOW YOUR SKILLZ — Là où tout a commencé", intro: "Un regard sur les performances, les gens et les moments qui ont rassemblé la communauté créative de Montréal.",
    vTitle: "Revivez notre dernière édition", vDesc: "Regardez le spectacle complet de la dernière édition de SHOW YOUR SKILLZ.", play: "Lire la vidéo", yt: "Regarder sur YouTube" },
};

function EventGallery({ c, lang }) {
  const [idx, setIdx] = useState(null);
  return (
    <div data-testid="sys-gallery">
      <p className="eyebrow mb-3">{c.eyebrow}</p>
      <h3 className="font-display text-3xl uppercase text-white sm:text-4xl" data-testid="sys-gallery-title">{c.title}</h3>
      <p className="mt-3 max-w-2xl text-base text-slate-400 md:text-lg" data-testid="sys-gallery-intro">{c.intro}</p>
      <div className="mt-8 columns-2 gap-3 sm:gap-4 lg:columns-4">
        {PHOTOS.map((p, i) => (
          <button key={p.id} onClick={() => setIdx(i)} data-testid={`sys-photo-${i}`} aria-label={p[`alt_${lang}`]}
            className="group mb-3 block w-full overflow-hidden rounded-xl border border-white/10 bg-black sm:mb-4">
            <img src={src(p.thumb_path)} alt={p[`alt_${lang}`]} width={p.w} height={p.h} loading="lazy" decoding="async"
              className="h-auto w-full transition-[transform,opacity] duration-500 group-hover:scale-[1.02] group-hover:opacity-90" />
          </button>
        ))}
      </div>
      {idx !== null && <Lightbox photos={PHOTOS} index={idx} onIndex={setIdx} onClose={() => setIdx(null)} srcOf={src} />}
    </div>
  );
}

function ShowVideo({ c }) {
  const [play, setPlay] = useState(false);
  return (
    <div className="mt-16" data-testid="sys-video">
      <h3 className="font-display text-3xl uppercase text-white sm:text-4xl" data-testid="sys-video-title">{c.vTitle}</h3>
      <p className="mt-3 text-base text-slate-400 md:text-lg" data-testid="sys-video-desc">{c.vDesc}</p>
      <div className="brackets relative mt-8 aspect-video w-full overflow-hidden rounded-2xl border border-white/10 bg-black">
        {play ? (
          <iframe src={`https://www.youtube.com/embed/${VIDEO_ID}?autoplay=1&rel=0`} title={c.vTitle} data-testid="sys-video-iframe"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin" className="absolute inset-0 h-full w-full border-0" />
        ) : (
          <button onClick={() => setPlay(true)} data-testid="sys-video-play" aria-label={c.play} className="group absolute inset-0">
            <img src="/story/video_poster.webp" alt={c.vTitle} loading="lazy" className="h-full w-full object-cover opacity-80 transition-opacity group-hover:opacity-100" />
            <span className="btn-red absolute left-1/2 top-1/2 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full transition-transform group-hover:scale-110 sm:h-20 sm:w-20"><Play size={28} className="ml-1" fill="currentColor" /></span>
          </button>
        )}
      </div>
      <a href={VIDEO_URL} target="_blank" rel="noopener noreferrer" data-testid="sys-video-youtube-link"
        className="mt-4 inline-flex items-center gap-2 text-sm text-slate-300 transition-colors hover:text-white"><Youtube size={18} className="text-red-500" />{c.yt}</a>
    </div>
  );
}

export default function StoryMedia() {
  const { lang } = useLang();
  const c = COPY[lang] || COPY.en;
  return (
    <div className="mt-20 border-t border-white/5 pt-16 lg:mt-24">
      <EventGallery c={c} lang={lang} />
      <ShowVideo c={c} />
    </div>
  );
}
